import {
  isDefined,
  sortByInputOrder,
  type ViewData,
  type ViewId,
  type ViewOrder,
  type ViewSurface,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { viewListKey } from './keys'
import { insertViewInOrder } from './insert'

type ViewCache = {
  set: (view: ViewData, order?: ViewOrder) => Promise<void>
  remove: (viewId: ViewId) => Promise<void>
  order: (surface: ViewSurface, viewIds: ViewOrder) => Promise<void>
}

export function useViewCache(): ViewCache {
  const client = useQueryClient()
  return {
    set: async (view, order): Promise<void> => {
      const queryKey = viewListKey(view.surface)
      await client.cancelQueries({ queryKey })
      client.setQueryData<ViewData[]>(queryKey, (views) => {
        if (views?.some((item) => item.viewId === view.viewId)) {
          return views.map((item) => (item.viewId === view.viewId ? view : item))
        }
        if (isDefined(order)) return insertViewInOrder({ views: views ?? [], view, order })
        return [...(views ?? []), view]
      })
      // The search-backed list can lag behind the mutation, so do not refetch it here.
    },
    order: async (surface, viewIds): Promise<void> => {
      const queryKey = viewListKey(surface)
      await client.cancelQueries({ queryKey })
      client.setQueryData<ViewData[]>(queryKey, (views) =>
        sortByInputOrder(views ?? [], viewIds, (view) => view.viewId)
      )
    },
    remove: async (viewId): Promise<void> => {
      await client.cancelQueries({ queryKey: viewListKey() })
      client.setQueriesData<ViewData[]>({ queryKey: viewListKey() }, (views) =>
        views?.filter((view) => view.viewId !== viewId)
      )
    },
  }
}
